import Image from "next/image";
import Link from "next/link";
import logo from "@/images/logo.png";
import logoDark from "@/images/logo-dark.png";
import AzeFlag from "@/images/aze-flag.svg";
import EngFlag from "@/images/en-flag.svg";
import { IoMenu } from "react-icons/io5";
import { FaCaretDown } from "react-icons/fa6";
import { useRouter } from "next/router";
import { Dropdown } from "flowbite-react";
import { useTranslation } from "next-i18next";
import { useState, useEffect, useRef } from "react";
import { trackApplyButton } from "@/utils/dataLayer";

const Navbar = ({ scrollToSection, setIsOpen }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const isPage =
    router.pathname === "/" ||
    router.pathname === "/contact-us" ||
    router.pathname === "/[slug]";
  const staticNavRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const [showFixedNavbar, setShowFixedNavbar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const prevScrollY = lastScrollYRef.current;
      const scrollingUp = currentScrollY < prevScrollY;
      const scrollingDown = currentScrollY > prevScrollY;

      const staticNav = staticNavRef.current;
      const staticNavBottom = staticNav
        ? staticNav.offsetTop + staticNav.offsetHeight
        : 100;
      const pastStaticNavbar = currentScrollY > staticNavBottom;

      if (!pastStaticNavbar || currentScrollY <= 10) {
        setShowFixedNavbar(false);
      } else if (scrollingUp) {
        setShowFixedNavbar(true);
      } else if (scrollingDown) {
        setShowFixedNavbar(false);
      }

      lastScrollYRef.current = currentScrollY;
    };

    lastScrollYRef.current = window.scrollY;
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isBlogPage = router.pathname === "/blogs" || router.pathname.startsWith("/blogs/");

  const changeLanguage = (locale) => async () => {
    if (isBlogPage) {
      const query = { ...router.query, lang: locale };
      await router.push({ pathname: router.pathname, query }, undefined, { locale });
    } else {
      await router.push({ pathname: router.route, query: router.query }, router.asPath, { locale });
    }
  };

  const theme = {
    arrowIcon: "hidden",
    floating: {
      base: "z-10 w-fit divide-y divide-gray-100 rounded-[8px] shadow",
      item: {
        base: "flex w-full cursor-pointer items-center justify-start px-4 py-2 text-[14px] hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b-[1.5px] border-[#F5F5F5]",
      },
      style: { auto: "border border-gray-200 bg-white text-gray-900" },
    },
    inlineWrapper: "flex items-center group",
  };

  const caretClass = (colorClass) =>
    `h-[15px] w-[15px] transition-transform duration-200 group-aria-expanded:rotate-180 ${colorClass}`;

  const NavbarContent = ({ variant }) => {
    const isFixed = variant === "fixed";
    const useDarkText = isFixed || !isPage;

    const LanguageSelector = ({ className = "" }) => (
      <Dropdown
        theme={theme}
        className={className}
        label={
          <div className="flex items-center gap-2 cursor-pointer">
            <Image
              src={router.locale === "en" ? EngFlag : AzeFlag}
              alt={`${router.locale}-flag`}
              className="rounded-sm"
              width={32}
              height={32}
              loading="lazy"
            />
            <FaCaretDown className={caretClass(useDarkText ? "text-[#1A3884]" : "text-white")} />
          </div>
        }
        inline
      >
        <Dropdown.Item
          className="hover:bg-[#F3F4F6] px-2 py-1 flex items-center justify-center"
          onClick={changeLanguage("az")}
        >
          <Image src={AzeFlag} alt="aze-flag" width={32} height={32} loading="lazy" />
        </Dropdown.Item>
        <Dropdown.Item
          className="hover:bg-[#F3F4F6] px-2 py-1 flex items-center justify-center"
          onClick={changeLanguage("en")}
        >
          <Image src={EngFlag} alt="en-flag" width={32} height={32} loading="lazy" />
        </Dropdown.Item>
      </Dropdown>
    );

    return (
      <div className="relative z-20 flex py-[30px] lg:py-[40px] w-full items-center justify-between max-w-[90%] mx-auto">
        <Link href="/">
          <Image
            src={useDarkText ? logoDark : logo}
            alt="logo"
            className="h-fit w-[125px] transition hover:opacity-80 lg:w-[185px]"
            priority={!isFixed}
          />
        </Link>

        <div className="hidden lg:flex items-center gap-0">
          <Dropdown
            theme={theme}
            label={
              <div className="flex items-center gap-2 cursor-pointer">
                <span
                  className={`${useDarkText ? "bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884] text-transparent" : "text-white"} text-[16px] hover:opacity-80 transition`}
                >
                  {t("navlinks.academy")}
                </span>
                <FaCaretDown className={caretClass(useDarkText ? "text-[#1A3884]" : "text-white")} />
              </div>
            }
            inline
          >
            <Link href="/about-us">
              <Dropdown.Item className="hover:bg-[#F3F4F6]">{t("navlinks.about-us")}</Dropdown.Item>
            </Link>
            <Link href="/methodology">
              <Dropdown.Item className="hover:bg-[#F3F4F6]">{t("navlinks.methodology")}</Dropdown.Item>
            </Link>
            <Link href="/branches">
              <Dropdown.Item className="hover:bg-[#F3F4F6]">{t("navlinks.branches")}</Dropdown.Item>
            </Link>
          </Dropdown>

          <div className={`h-[14px] w-[2px] ${useDarkText ? "bg-gray-300" : "bg-gray-500"} mx-[20px]`} />

          <Dropdown
            theme={theme}
            label={
              <div className="flex items-center gap-2 cursor-pointer">
                <span
                  className={`${useDarkText ? "bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884] text-transparent" : "text-white"} text-[16px] hover:opacity-80 transition`}
                >
                  {t("navlinks.programs")}
                </span>
                <FaCaretDown className={caretClass(useDarkText ? "text-[#1A3884]" : "text-white")} />
              </div>
            }
            inline
          >
            <Link href="/programs">
              <Dropdown.Item className="hover:bg-[#F3F4F6]">{t("navlinks.all-programs")}</Dropdown.Item>
            </Link>
            <Link href="/programs/front-end">
              <Dropdown.Item className="hover:bg-[#F3F4F6]">{t("navlinks.programs-frontend")}</Dropdown.Item>
            </Link>
            <Link href="/programs/back-end">
              <Dropdown.Item className="hover:bg-[#F3F4F6]">{t("navlinks.programs-backend")}</Dropdown.Item>
            </Link>
            <Link href="/programs/full-stack">
              <Dropdown.Item className="hover:bg-[#F3F4F6]">{t("navlinks.programs-fullstack")}</Dropdown.Item>
            </Link>
            <Link href="/programs/software-engineering">
              <Dropdown.Item className="hover:bg-[#F3F4F6]">
                {t("navlinks.programs-softwareengineering")}
              </Dropdown.Item>
            </Link>
            <Link href="/programs/summer-bootcamp">
              <Dropdown.Item className="hover:bg-[#F3F4F6]">{t("navlinks.programs-summerbootcamp")}</Dropdown.Item>
            </Link>
          </Dropdown>

          <div className={`h-[14px] w-[2px] ${useDarkText ? "bg-gray-300" : "bg-gray-500"} mx-[20px]`} />

          <Link
            href="/blogs"
            className={`${useDarkText ? "bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884] text-transparent" : "text-white"} text-[16px] hover:opacity-80 transition`}
          >
            {t("navlinks.blogs")}
          </Link>

          <div className={`h-[14px] w-[2px] ${useDarkText ? "bg-gray-300" : "bg-gray-500"} mx-[20px]`} />

          <Link
            href="/contact-us"
            className={`${useDarkText ? "bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884] text-transparent" : "text-white"} text-[16px] hover:opacity-80 transition`}
          >
            {t("navlinks.contact-us")}
          </Link>
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <LanguageSelector className="!left-[-8px] !top-[-5px]" />
          <Link href="/apply" onClick={() => trackApplyButton("navbar")}>
            <button
              className={`relative flex items-center justify-center rounded-full overflow-hidden group transition-all duration-300 ${useDarkText ? "bg-gradient-to-b from-[#091F59] to-[#1A3884] text-white hover:text-[#091F59] border border-transparent hover:border-[#091F59]" : "bg-white text-gray-900 hover:text-white border border-white"} w-[130px] h-[40px] text-[14px] font-[600]`}
            >
              <span className="relative z-10">{t("apply-btn")}</span>
              <span
                className={`absolute inset-0 transition-opacity duration-300 rounded-full ${useDarkText ? "bg-white opacity-0 group-hover:opacity-100" : "bg-[#091F59] opacity-0 group-hover:opacity-100"}`}
              />
            </button>
          </Link>
        </div>

        <div className="flex lg:hidden items-center gap-4">
          <LanguageSelector className="!left-[-12px] lg:!left-[-14px] !top-[-5px]" />
          <button
            onClick={() => setIsOpen(true)}
            className="cursor-pointer flex items-center justify-center"
            aria-label="Open menu"
          >
            <IoMenu className={`text-[30px] ${useDarkText ? "text-[#1A3884]" : "text-white"}`} aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <nav
        ref={staticNavRef}
        id="main-page-start"
        className={`relative z-[100] ${isPage ? "bg-transparent" : "bg-white/80 backdrop-blur-md"}`}
      >
        <NavbarContent variant="static" />
      </nav>

      <nav
        className={`fixed top-0 left-0 right-0 z-[110] bg-white/80 backdrop-blur-md rounded-b-[8px] transition-transform duration-300 ${showFixedNavbar ? "translate-y-0" : "-translate-y-full pointer-events-none"}`}
        aria-hidden={!showFixedNavbar}
      >
        <NavbarContent variant="fixed" />
      </nav>
    </>
  );
};

export default Navbar;
